import { useState, useCallback, useEffect } from "react";
import useSWR from "swr";
import {
  useFetchAuthenticate,
  useAPIAuthenticate,
  useToast,
  useAPIAuthenticateToast,
  usePostFetchAuthenticate,
} from "hooks";
import { getURLWithParams } from "utils/context";
import { validatePassword } from "utils/utils";
import { USER_TYPE } from "constants/Types/index";

const CredentialsUser = {
  Atendente: "attendant",
  Administrador: "admin",
  Gerente: "manager",
  Supervisor: "financial",
  dev: "dev",
  root: "root",
};

export const useUsers = (params = {}) =>
  useFetchAuthenticate(params ? getURLWithParams("/users", params) : null);

export const useUsersU = (params = {}) =>
  useFetchAuthenticate(params ? getURLWithParams("/users", params) : null);

export const useUser = id => useFetchAuthenticate(id ? `/user/${id}` : "/user");

export const useUserList = params =>
  usePostFetchAuthenticate(params ? "users/ids" : null, params);

export const useUserRemove = () => {
  const { isLoading, onSend } = useAPIAuthenticateToast();

  const onRemove = id =>
    onSend({
      endpoint: `/user/${id}`,
      messageSuccess: "Usuario foi deletado com sucesso!",
      messageFail: "Não foi possível deletar o usuário!",
      type: "DELETE",
    });

  return {
    onRemove,
    isLoading,
  };
};

export const useUserToggleActive = () => {
  const { isLoading, onSend } = useAPIAuthenticateToast();
  const api = useAPIAuthenticate();

  // escrituracaoStatus/action/statusBefore são opcionais: quem não passa
  // continua só mudando isActive, como antes (ex: outras telas que usam
  // esse hook). action+statusBefore só existem pra alimentar o log de
  // ações (POST /user/action-log), registrado depois do PUT ter sucesso.
  const onToggleActive = async (
    id,
    isActive,
    escrituracaoStatus,
    action,
    statusBefore,
    userName,
  ) => {
    const result = await onSend({
      endpoint: `/user/${id}`,
      type: "PUT",
      data: escrituracaoStatus
        ? { isActive, escrituracaoStatus }
        : { isActive },
      messageSuccess: isActive
        ? "Usuário ativado com sucesso!"
        : "Usuário congelado com sucesso!",
      messageFail: "Não foi possível alterar o status do usuário.",
    });

    if (result?.response && action && escrituracaoStatus) {
      api
        .post("/user/action-log", {
          userId: id,
          userName,
          action,
          statusBefore: statusBefore ?? null,
          statusAfter: escrituracaoStatus,
        })
        .catch(() => {});
    }

    return result;
  };

  return { onToggleActive, isLoading };
};

// Resolve o(s) usuário(s) Mongo vinculados a um CNPJ de contrato/cliente
// (GET /stores?cnpj= -> store._id -> GET /users/store/:id, que não traz
// isActive) e busca o isActive real de cada um via GET /user/:id.
// Usa SWR: Status e Ações da mesma linha compartilham a mesma requisição
// e o `revalidate` deixa a UI consistente logo após um toggle.
export const useContratoUserStatus = cnpj => {
  const api = useAPIAuthenticate();

  const { data, error, mutate } = useSWR(
    cnpj ? `contrato-user-status-${cnpj}` : null,
    async () => {
      const { data: storesData } = await api.get("/stores", {
        params: { cnpj, page: 1, limit: 5 },
      });
      const store = storesData?.stores?.docs?.[0];

      if (!store?._id) {
        return { store: null, users: [], isActive: null };
      }

      const { data: usersData } = await api.get(`/users/store/${store._id}`);
      const users = usersData?.data ?? [];

      if (!users.length) {
        return { store, users: [], isActive: null };
      }

      const detailed = await Promise.all(
        users.map(u => api.get(`/user/${u._id}`).then(res => res.data?.user)),
      );

      return {
        store,
        users: detailed,
        isActive: detailed.some(u => u?.isActive),
      };
    },
  );

  return {
    store: data?.store,
    users: data?.users,
    isActive: data?.isActive,
    isLoading: !error && !data,
    revalidate: mutate,
  };
};

// O status exibido segue isActive, que é o que o login checa para bloquear
// o acesso; escrituracaoStatus só diz se o bloqueio foi Suspender ou
// Cancelar. Bloqueado sem esse motivo (ex: congelado em outra tela) fica
// sem status e aparece como "Inativo".
const getAcessoStatus = user => {
  if (user?.isActive !== false) {
    return "ativo";
  }

  return ["suspenso", "cancelado"].includes(user?.escrituracaoStatus)
    ? user.escrituracaoStatus
    : null;
};

// Usuários da loja (mesmo cache de useContratoUserStatus, sem refetch extra)
// anotados com o grupo de acesso *dessa loja* e se ele é de Escrituração —
// só esses entram no select de Ativar/Suspender/Cancelar. Checar por módulo
// habilitado pega grupos genéricos demais (ex: "Geral MIX", que tem 161
// módulos, incluindo o de Escrituração incidentalmente); usamos o nome do
// grupo em vez disso.
export const useStoreUsersByAccessGroup = cnpj => {
  const { store, users, isLoading, revalidate } = useContratoUserStatus(cnpj);

  const usersWithGroup = (users ?? []).map(user => {
    const groupPermission = (user?.groupPermissions ?? []).find(
      gp => gp?.store === store?._id,
    );
    const grupo = groupPermission?.grupo;
    const groupName = grupo?.groupName ?? "Sem grupo de acesso";

    return {
      _id: user?._id,
      name: user?.name,
      surname: user?.surname,
      email: user?.email,
      isActive: user?.isActive,
      escrituracaoStatus: getAcessoStatus(user),
      groupName,
      isEscrituracao: groupName.toLowerCase().includes("escritura"),
      isMix: !!user?.is_mix,
    };
  });

  return { store, users: usersWithGroup, isLoading, revalidate };
};

export const useWalletUser = (id, params = {}) =>
  useFetchAuthenticate(
    id ? getURLWithParams(`/user/wallet/${id}`, params) : null,
  );

export const useUserWallet = () => {
  const api = useAPIAuthenticate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const fetch = useCallback(
    id => {
      setLoading(true);
      const url = id || ``;
      const fetchData = async () => {
        try {
          const {
            data: { wallet },
          } = await api.get(`/user/wallet/${url}`);

          if (wallet.docs === 0) {
            addToast({
              type: "warning",
              align: "top",
              title: "Ops...",
              description: "Nehuma Carteira de Cliente encontrada",
            });
          }

          setData(wallet.docs);
          setLoading(false);
        } catch (e) {
          setLoading(false);
        }
      };
      fetchData(url);
    },
    [api, addToast],
  );

  return {
    data,
    fetch,
    isLoading: loading,
  };
};

export const useUserGet = ({ id } = {}) => {
  const api = useAPIAuthenticate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(``);
  const fetch = useCallback(
    (params = {}) => {
      setLoading(true);
      setIsError(`loading`);
      const url = id ?? params.id ?? ``;
      const fetchData = async () => {
        try {
          if (url === "") {
            return false;
          }
          const {
            data: { user },
          } = await api.get(`/user/${url}`);
          setData(user);
        } catch (e) {
          setIsError(``);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    },
    [api, id],
  );

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    data,
    fetch,
    isError,
    isLoading: loading,
  };
};

export const useUserCrud = item => {
  const api = useAPIAuthenticate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(``);

  const { addToast } = useToast();

  const fetch = useCallback(
    (value = {}, tableData) => {
      setLoading(true);

      const validation = validatePassword(value?.password);

      if (!validation && (!value?._id || value?.password)) {
        return addToast({
          title: "Usuário error senha",
          type: "error",
          description: `Precisa conter 8 caracteres, letras minúsculas,
                letras maiúsculas, números e caracteres especiais.`,
        });
      }

      if (value.password !== value.confirmPassword) {
        return addToast({
          title: "Usuário",
          type: "error",
          description: `Os campos de senha não coincidem`,
        });
      }

      const fetchData = async () => {
        try {
          if (item.id) {
            const newTableData = JSON.parse(JSON.stringify(tableData)).map(
              tableItem => {
                const newTableItem = JSON.parse(JSON.stringify(tableItem));
                delete newTableItem._id;
                return newTableItem;
              },
            );
            try {
              const fields = {
                ...data,
                officeHour: {
                  afterHoursAccess: Boolean(value.officeHour),
                  entryTime: value.enteringTime,
                  departureTime: value.leavingTime,
                },
                permissions: {
                  export: {
                    xml: !!value.export_xml,
                    pdf: !!value.export_pdf,
                    csv: !!value.export_csv,
                    txt: !!value.export_txt,
                  },
                  import: {
                    xml: !!value.import_xml,
                    pdf: !!value.import_pdf,
                    csv: !!value.import_csv,
                    txt: !!value.import_txt,
                  },
                },
                is_mix: value.is_mix || false,
                contractService: value.hireService,
                isActive: Boolean(value.is_active),
                whatsAppNumber: value.whatsAppNumber,
                acceptInvite: value.inviteThroughEmail,
                name: value.name,
                surname: value.surname,
                email: value.email,
                responsibility: value.role,
                type: CredentialsUser[value.role],
                groupPermissions: newTableData,
              };

              if ((item.id && value.password) || value.password) {
                fields.password = value.password;
              }

              const { data: updatedData } = await api.put(
                `/user/${item.id}`,
                fields,
              );

              setData(updatedData.user);
              addToast({
                title: "Usuário",
                type: "success",
                description: "Usuário atualizado com sucesso!",
              });
            } catch (err) {
              addToast({
                title: "Usuário",
                type: "error",
                description: `Ocorreu o seguinte erro: ${err.message}`,
              });
              setIsError(err);
            }
          } else {
            const newTableData = JSON.parse(JSON.stringify(tableData)).map(
              tableItem => {
                const newTableItem = JSON.parse(JSON.stringify(tableItem));
                delete newTableItem._id;
                return newTableItem;
              },
            );
            try {
              await api.post(`/user`, {
                officeHour: {
                  afterHoursAccess: Boolean(value.officeHour),
                  entryTime: value.enteringTime,
                  departureTime: value.leavingTime,
                },
                permissions: {
                  export: {
                    xml: !!value.export_xml,
                    pdf: !!value.export_pdf,
                    csv: !!value.export_csv,
                    txt: !!value.export_txt,
                  },
                  import: {
                    xml: !!value.import_xml,
                    pdf: !!value.import_pdf,
                    csv: !!value.import_csv,
                    txt: !!value.import_txt,
                  },
                },
                is_mix: value.is_mix || false,
                contractService: value.hireService,
                isActive: Boolean(value.is_active.value),
                acceptInvite: value.inviteThroughEmail,
                whatsAppNumber: value.whatsAppNumber,
                name: value.name,
                surname: value.surname,
                email: value.email,
                responsibility: value.role,
                type: CredentialsUser[value.role],
                password: value.password,
                groupPermissions: newTableData,
              });
              addToast({
                title: "Usuário",
                type: "success",
                description: "Usuário criado com sucesso!",
              });
            } catch (err) {
              if (value.password === "") {
                addToast({
                  title: "Usuário",
                  type: "error",
                  description: `Preencha os campos de Senha e Confirmar Senha!`,
                });
              } else {
                setIsError(err);
                addToast({
                  title: "Usuário",
                  type: "error",
                  description: `Ocorreu o seguinte erro: ${err.message}`,
                });
              }
            }
          }
        } catch (e) {
          setIsError(e);
          setLoading(false);
          setIsError(``);
        }
      };
      fetchData();
    },
    [api, addToast, item, data],
  );
  return {
    data,
    fetch,
    isError,
    isLoading: loading,
  };
};

export const useUsersAssessores = () =>
  useFetchAuthenticate("/user/list/assessor");
export const useUserCrud2 = item => {
  const api = useAPIAuthenticate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(``);

  const { addToast } = useToast();

  const fetch = useCallback(
    (value = {}) => {
      setLoading(true);

      const validation = validatePassword(value?.password);

      if (
        value?.password?.length > 0 &&
        !validation &&
        (!value?._id || value?.password)
      ) {
        return addToast({
          title: "Usuário error senha",
          type: "error",
          description: `Precisa conter 8 caracteres, letras minúsculas,
                  letras maiúsculas, números e caracteres especiais.`,
        });
      }

      if (
        value?.password?.length > 0 &&
        value?.password !== undefined &&
        value.password !== value?.confirmPassword
      ) {
        return addToast({
          title: "Usuário",
          type: "error",
          description: `Os campos de senha não coincidem`,
        });
      }

      const fetchData = async () => {
        try {
          if (item?.id) {
            try {
              const fields = {
                ...data,
                officeHour: {
                  afterHoursAccess: Boolean(value?.officeHour),
                  entryTime: value?.enteringTime,
                  departureTime: value?.leavingTime,
                },
                permissions: {
                  export: {
                    xml: !!value?.export_xml,
                    pdf: !!value?.export_pdf,
                    csv: !!value?.export_csv,
                    txt: !!value?.export_txt,
                  },
                  import: {
                    xml: !!value?.import_xml,
                    pdf: !!value?.import_pdf,
                    csv: !!value?.import_csv,
                    txt: !!value?.import_txt,
                  },
                },
                is_mix: value?.is_mix || false,
                contractService: value?.hireService,
                isActive: Boolean(value?.isActive),
                whatsAppNumber: value?.whatsAppNumber,
                acceptInvite: value?.inviteThroughEmail,
                name: value?.name,
                surname: value?.lastName || value?.surname,
                email: value?.email,
                responsibility: value?.role,
                type: USER_TYPE[value?.role],
                groupPermissions: value?.groupPermissions,
              };

              if ((item?.id && value?.password) || value?.password) {
                fields.password = value?.password;
              }

              const { data: updatedData } = await api.put(
                `/user/${item?.id}`,
                fields,
              );

              setData(updatedData.user);
              addToast({
                title: "Usuário",
                type: "success",
                description: "Usuário atualizado com sucesso!",
              });
            } catch (err) {
              addToast({
                title: "Usuário",
                type: "error",
                description: `Ocorreu o seguinte erro: ${err?.message}`,
              });
              setIsError(err);
            }
          } else {
            try {
              await api.post(`/user`, {
                officeHour: {
                  afterHoursAccess: Boolean(value.officeHour),
                  entryTime: value.enteringTime,
                  departureTime: value.leavingTime,
                },
                permissions: {
                  export: {
                    xml: !!value.export_xml,
                    pdf: !!value.export_pdf,
                    csv: !!value.export_csv,
                    txt: !!value.export_txt,
                  },
                  import: {
                    xml: !!value.import_xml,
                    pdf: !!value.import_pdf,
                    csv: !!value.import_csv,
                    txt: !!value.import_txt,
                  },
                },
                is_mix: value.is_mix || false,
                contractService: value.hireService,
                isActive: Boolean(value.isActive),
                acceptInvite: value.inviteThroughEmail,
                whatsAppNumber: value.whatsAppNumber,
                name: value.name,
                surname: value.surname,
                email: value.email,
                responsibility: value.role,
                type: USER_TYPE[value.role],
                password: value.password,
                groupPermissions: value?.groupPermissions,
              });
              addToast({
                title: "Usuário",
                type: "success",
                description: "Usuário criado com sucesso!",
              });
            } catch (err) {
              if (value?.password === "") {
                addToast({
                  title: "Usuário",
                  type: "error",
                  description: `Preencha os campos de Senha e Confirmar Senha!`,
                });
              } else {
                setIsError(err);
                addToast({
                  title: "Usuário",
                  type: "error",
                  description: `Ocorreu o seguinte erro: ${err?.message}`,
                });
              }
            }
          }
        } catch (e) {
          setIsError(e);
          setLoading(false);
          setIsError(``);
        }
      };
      fetchData();
    },
    [api, addToast, item, data],
  );
  return {
    data,
    fetch,
    isError,
    isLoading: loading,
  };
};

export const useUserLog = () => {
  const { onSend } = useAPIAuthenticateToast();

  const onLogout = (data, callback) =>
    onSend({
      endpoint: `/logout`,
      data,
      isDisableMsg: true,
    }).finally(() => callback());

  return {
    onLogout,
  };
};

export const useUploadUserImage = () => {
  const api = useAPIAuthenticate();

  const { onSend } = useAPIAuthenticateToast();

  const onRemove = id =>
    onSend({
      endpoint: `/user/${id}`,
      data: {},
      isDisableMsg: true,
    }).finally(() => {});

  const uploadImage = async (userId, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.put(`/user/${userId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const userFields = JSON.parse(localStorage.getItem("userData")) || {};

      if (response?.data?.response === undefined) {
        throw new Error("Resposta inválida do servidor");
      }

      userFields.image = response?.data?.url;

      localStorage.setItem("userData", JSON.stringify(userFields));

      return response.data;
    } catch (error) {
      console.error("Erro no useUploadUserImage:", error);
      console.error("Error response:", error.response);
      throw error;
    }
  };

  return {
    uploadImage,
    onRemove,
  };
};

export const useGetLoginLogoutLogByUserId = (id, params = {}) =>
  useFetchAuthenticate(getURLWithParams(`/user/logs/${id}`, params));
